import { FocusEventHandler, useCallback, useEffect, useState } from 'react'

import ActivityInputAgreementTerm from './ActivityInputAgreementTerm/ActivityInputAgreementTerm'
import ActivityInputDate from './ActivityInputDate'
import ActivityInputFileS3 from './ActivityInputFileS3'
import ActivityInputInteger from './ActivityInputInteger'
import ActivityInputSelect from './ActivityInputSelect'
import ActivityInputString from './ActivityInputString'
import ActivityInputTakePhoto from './ActivityInputTakePhoto'
import ActivityInputTakeVideo from './ActivityInputTakeVideo'

import {
  ActivityModel,
  ActivityValue,
  FlatActivityModel,
  StatusClass,
  ValidationStatus
} from 'types/shared'
import '../../../../asset/css/form.css'
import ActivityInputDraw from './ActivityInputDraw/ActivityInputDraw'
import { VALIDATION_STATUSES } from '../../../../utils/constants'
import useBlueprintsContext from '../../hooks/useBlueprintsContext'
import SiteRenderer from '../SiteRenderer'
import ActivityMultiselectionAccordion, {
  ActivityMultiselectionAccordionProps
} from './AcitivityExplicitMultiselectionAccordion/ActivityMultiselectionAccordion'
import ActivityExplicitUniselectionAccordion, {
  ActivityExplicitUniselectionAccordionProps
} from './AcitivityExplicitUniselectionAccordion/ActivityUniselectionAccordion'
import { ActivityInputProps } from './Activity.types'
import ActivityAutocompleteMultiselection from './ActivityAutocompleteMultiselection/ActivityAutocompleteMultiselection'
import ActivityAutocompleteUniselection from './ActivityAutocompleteUniselection/ActivityAutocompleteUniselection'
import ActivityDocumentFile from './ActivityDocumentFile/ActivityDocumentFile'
import ActivityDocumentPhoto from './ActivityDocumentPhoto'
import ActivityMarkPhoto from './ActivityMarkPhoto'
import ActivityDocumentVideo from './ActivityDocumentVideo'
import ActivityDocumentAudio from './ActivityDocumentAudio/ActivityDocumentAudio'
import ActivityExplicitMultiselection from './ActivityExplicitMultiselection/ActivityExplicitMultiselection'
import ActivityExplicitUniselection from './ActivityExplicitUniselection/ActivityExplicitUniselection'
import ActivityGroup from './ActivityGroup/ActivityGroup'
import ActivityInputArea from './ActivityInputArea'
import ActivityInputRichText from './ActivityInputRichText'
import ActivityInputGeolocation from './ActivityInputGeolocation'
import ActivityOTP from './ActivityOTP/ActivityOTP'
import ActivityStandardMultiselection, {
  ActivityStandardMultiselectionProps
} from './ActivityStandardMultiselection/ActivityStandardMultiselection'
import ActivityStandardUniselection, {
  ActivityStandardUniselectionProps
} from './ActivityStandardUniselection/ActivityStandardUniselection'
import ActivityYta from './ActivityYta'
import ActivitySafeHTML from './ActivitySafeHTML/ActivitySafeHTML'
import ActivityActionButton from './ActivityActionButton'
import useValidateActivity, {
  useValidateActivityCallback
} from 'modules/interaction/hooks/useValidateActivity/useValidateActivity'
import ActivityInputAnimatedWrapper from './ActivityInputAnimatedWrapper'
import ActivityInteractionReview from './ActivityInteractionReview/ActivityInteractionReview'
import ActivityEmbeddedSitemodel from './ActivityEmbeddedSitemodel'
import ActivityMultipleAnswers from './ActivityMultipleAnswers'
import { useAppSelector } from 'state/store'
import ActivityEvaluation from './ActivityEvaluation/ActivityEvaluation'
import ActivityStarsEvaluation from './ActivityStarsEvaluation/ActivityStarsEvaluation'

const EMPTY_STRING_ARRAY: string[] = []

const ActivityInput = ({
  activityModelInput,
  value,
  parentPath = EMPTY_STRING_ARRAY,
  inputClass,
  statusClass,
  currentStepUuid,
  isFetchingBlueprints = false,
  handleChange,
  disabled
}: ActivityInputProps) => {
  const { informational: informationalInteraction } = useAppSelector(
    (state) => state.interactionModel
  )

  const informational =
    informationalInteraction || activityModelInput.informational

  const stringInputValue = (typeof value === 'object' ? value.des : value) || ''

  if (activityModelInput.content_type === 'safe_html') {
    return <ActivitySafeHTML htmlContent={activityModelInput.html_content} />
  }

  if (activityModelInput.content_type === 'action_button') {
    return <ActivityActionButton activityModel={activityModelInput} />
  }

  if (activityModelInput.content_type === 'interaction_review') {
    return <ActivityInteractionReview activityModel={activityModelInput} />
  }

  if (activityModelInput.content_type === 'integer') {
    return (
      <ActivityInputInteger
        id={activityModelInput.uuid}
        cssClass={`${inputClass}`}
        value={stringInputValue}
        required={activityModelInput.required}
        onChange={handleChange}
        label={activityModelInput.display_text.des}
        informational={informational}
        helpText={activityModelInput.help_text?.des}
        explanationText={activityModelInput.explanation_text?.des}
        formatMask={activityModelInput.format_mask}
        disabled={disabled}
        actionButton={activityModelInput.action_button}
      />
    )
  }

  if (activityModelInput.content_type === 'date') {
    return (
      <ActivityInputDate
        id={activityModelInput.uuid}
        statusClass={statusClass}
        value={value as string}
        format='date'
        required={activityModelInput.required}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
        helpText={activityModelInput.help_text?.des}
        explanationText={activityModelInput.explanation_text?.des}
        {...{ isFetchingBlueprints }}
        minimumYear={activityModelInput.minimum_year}
        maximumYear={activityModelInput.maximum_year}
        minFromNow={activityModelInput.min_from_now}
        maxFromNow={activityModelInput.max_from_now}
        disabled={disabled}
        informational={informational}
      />
    )
  }

  if (activityModelInput.content_type === 'datetime') {
    return (
      <ActivityInputDate
        id={activityModelInput.uuid}
        statusClass={statusClass}
        value={value as string}
        format='date-time'
        required={activityModelInput.required}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
        helpText={activityModelInput.help_text?.des}
        explanationText={activityModelInput.explanation_text?.des}
        {...{ isFetchingBlueprints }}
        minimumYear={activityModelInput.minimum_year}
        maximumYear={activityModelInput.maximum_year}
        minFromNow={activityModelInput.min_from_now}
        maxFromNow={activityModelInput.max_from_now}
        disabled={disabled}
        informational={informational}
      />
    )
  }

  if (activityModelInput.content_type === 'time') {
    return (
      <ActivityInputDate
        id={activityModelInput.uuid}
        statusClass={statusClass}
        value={value as string}
        format='time'
        required={activityModelInput.required}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
        helpText={activityModelInput.help_text?.des}
        explanationText={activityModelInput.explanation_text?.des}
        {...{ isFetchingBlueprints }}
        minFromNow={activityModelInput.min_from_now}
        maxFromNow={activityModelInput.max_from_now}
        disabled={disabled}
      />
    )
  }

  if (activityModelInput.content_type === 'datepicker') {
    return (
      <ActivityInputDate
        id={activityModelInput.uuid}
        statusClass={statusClass}
        value={value as string}
        format={activityModelInput.format || 'date-time'}
        required={activityModelInput.required}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
        helpText={activityModelInput.help_text?.des}
        explanationText={activityModelInput.explanation_text?.des}
        {...{ isFetchingBlueprints }}
        minimumYear={activityModelInput.minimum_year}
        maximumYear={activityModelInput.maximum_year}
        minFromNow={activityModelInput.min_from_now}
        maxFromNow={activityModelInput.max_from_now}
        disabled={disabled}
      />
    )
  }

  /**
   * @deprecated
   */
  if (activityModelInput.content_type === 'file') {
    return (
      <ActivityInputFileS3
        id={activityModelInput.uuid}
        cssClass={`${inputClass}`}
        inputValue={stringInputValue}
        onChange={handleChange}
        required={activityModelInput.required}
        label={activityModelInput.display_text?.des}
        informational={informational}
      />
    )
  }

  /**
   * @deprecated
   */
  if (activityModelInput.content_type === 'take_photo') {
    return (
      <ActivityInputTakePhoto
        inputValue={stringInputValue}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
      />
    )
  }

  /**
   * Non-standard component
   */
  if (activityModelInput.content_type === 'take_video') {
    return (
      <ActivityInputTakeVideo
        inputValue={stringInputValue}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
      />
    )
  }

  if (activityModelInput.content_type === 'agreement_term') {
    return (
      <ActivityInputAgreementTerm
        onChange={handleChange}
        inputValue={stringInputValue}
        terms={activityModelInput.display_text?.des}
        dataContainerVariable={activityModelInput.data_container_variable}
      />
    )
  }

  if (
    activityModelInput.content_type === 'draw' ||
    activityModelInput.content_type === 'digital_signature'
  ) {
    return (
      <ActivityInputDraw
        inputValue={stringInputValue}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
        explanation={activityModelInput.explanation_text?.des}
        agreement={activityModelInput.agreement}
        informational={informational}
        data_container_variable={activityModelInput.data_container_variable}
      />
    )
  }

  if (activityModelInput.content_type === 'activity_group') {
    return (
      <ActivityGroup
        currentActivityModel={activityModelInput}
        parentPath={parentPath}
        onChange={handleChange}
        label={activityModelInput.display_text.des}
        currentStepUuid={currentStepUuid}
        ruleSet={activityModelInput.rule_set}
      />
    )
  }

  if (activityModelInput.content_type === 'document_photo') {
    return (
      <ActivityDocumentPhoto
        uuid={activityModelInput.uuid}
        inputValue={stringInputValue}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
        required={activityModelInput.required}
        maxSizeInBytes={activityModelInput.max_size_in_bytes}
        instructionModal={activityModelInput.instruction_modal}
        instructionImageUrl={activityModelInput.instruction_image_url}
        hideDescription={activityModelInput.hide_description}
        videoHeight={activityModelInput.video_dimensions?.height}
        videoWidth={activityModelInput.video_dimensions?.width}
      />
    )
  }

  if (activityModelInput.content_type === 'mark_photo') {
    return (
      <ActivityMarkPhoto
        uuid={activityModelInput.uuid}
        inputValue={stringInputValue}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
        required={activityModelInput.required}
        maxSizeInBytes={activityModelInput.max_size_in_bytes}
        categoriesFilteraction={
          activityModelInput.information_request_filteraction
        }
        isFullscreenMarking={activityModelInput.is_fullscreen_marking}
        markingPromptTitle={activityModelInput.marking_prompt_title}
        categorySelectionModal={activityModelInput.category_selection_modal}
        hideDescription={activityModelInput.hide_description}
        markColor={activityModelInput.mark_color}
        videoHeight={activityModelInput.video_dimensions?.height}
        videoWidth={activityModelInput.video_dimensions?.width}
      />
    )
  }

  if (activityModelInput.content_type === 'document_video') {
    return (
      <ActivityDocumentVideo
        uuid={activityModelInput.uuid}
        inputValue={stringInputValue}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
        required={activityModelInput.required}
        hideDescription={activityModelInput.hide_description}
        videoHeight={activityModelInput.video_dimensions?.height}
        videoWidth={activityModelInput.video_dimensions?.width}
      />
    )
  }

  if (activityModelInput.content_type === 'document_audio') {
    return (
      <ActivityDocumentAudio
        uuid={activityModelInput.uuid}
        inputValue={stringInputValue}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
        required={activityModelInput.required}
        maxSizeInBytes={activityModelInput.max_size_in_bytes}
        accept={activityModelInput.accept}
        hideDescription={activityModelInput.hide_description}
        actions={activityModelInput.actions}
      />
    )
  }

  if (activityModelInput.content_type === 'document_file') {
    return (
      <ActivityDocumentFile
        uuid={activityModelInput.uuid}
        inputValue={value || ''}
        onChange={handleChange}
        label={activityModelInput.display_text?.des}
        required={activityModelInput.required}
        actions={activityModelInput.actions}
        maxSizeInBytes={activityModelInput.max_size_in_bytes}
        accept={activityModelInput.accept}
        explanationText={activityModelInput.explanation_text?.des}
        hideDescription={activityModelInput.hide_description}
      />
    )
  }

  if (activityModelInput.content_type === 'textarea') {
    return (
      <ActivityInputArea
        id={activityModelInput.uuid}
        statusClass={statusClass}
        value={stringInputValue}
        required={activityModelInput.required}
        onChange={handleChange}
        label={activityModelInput.display_text.des}
        informational={informational}
        helpText={activityModelInput.help_text?.des}
        explanationText={activityModelInput.explanation_text?.des}
        disabled={disabled}
      />
    )
  }

  if (activityModelInput.content_type === 'rich_text') {
    return (
      <ActivityInputRichText
        id={activityModelInput.uuid}
        statusClass={statusClass}
        value={stringInputValue}
        required={activityModelInput.required}
        onChange={handleChange}
        label={activityModelInput.display_text.des}
        informational={informational}
        disabled={disabled}
        tools={activityModelInput.tools}
        placeholder={activityModelInput.explanation_text?.des}
      />
    )
  }

  if (activityModelInput.content_type === 'explicit_uniselection_objectclass') {
    return (
      <ActivityExplicitUniselection
        id={activityModelInput.uuid}
        parentPath={parentPath}
        value={value || ''}
        options={activityModelInput.selection_options}
        filteraction={activityModelInput.information_request_filteraction}
        label={activityModelInput.display_text.des}
        direction={activityModelInput.direction || 'vertical'}
        optionLabelsPosition={
          activityModelInput.optionLabelsPosition || 'right'
        }
        required={activityModelInput.required}
        onChange={handleChange}
        disabled={disabled}
      />
    )
  }

  if (
    activityModelInput.content_type === 'explicit_multiselection_objectclass'
  ) {
    return (
      <ActivityExplicitMultiselection
        id={activityModelInput.uuid}
        parentPath={parentPath}
        value={value || ''}
        options={activityModelInput.selection_options}
        filteraction={activityModelInput.information_request_filteraction}
        label={activityModelInput.display_text.des}
        direction={activityModelInput.direction || 'vertical'}
        optionLabelsPosition={
          activityModelInput.optionLabelsPosition || 'right'
        }
        required={activityModelInput.required}
        onChange={handleChange}
        disabled={disabled}
      />
    )
  }

  if (activityModelInput.content_type === 'geolocation') {
    return (
      <ActivityInputGeolocation
        id={activityModelInput.uuid}
        label={activityModelInput.display_text.des}
        value={stringInputValue}
        required={activityModelInput.required}
        dependencies={activityModelInput.geolocation_dependencies}
        onChange={handleChange}
        parentPath={parentPath}
        fillGeolocationValues={activityModelInput.fill_geolocation_values}
      />
    )
  }

  /* Não está validado com ActivityGroup */
  if (activityModelInput.content_type === 'yta') {
    return (
      <ActivityYta
        sourceDataContainerVariable={
          activityModelInput.yta?.source_data_container_variable
        }
      />
    )
  }

  if (activityModelInput.content_type === 'autocomplete_multiselection') {
    return (
      <ActivityAutocompleteMultiselection
        parentPath={parentPath}
        statusClass={statusClass}
        filteraction={activityModelInput.information_request_filteraction}
        filteractionParams={
          activityModelInput.information_request_filteraction_query_params
        }
        dataSources={activityModelInput.data_sources}
        endpoint={activityModelInput.information_request_callback}
        value={value || ''}
        actions={activityModelInput.actions || []}
        onChange={handleChange}
        required={activityModelInput.required}
        label={activityModelInput.display_text.des}
        informational={informational}
        placeholder={activityModelInput.explanation_text?.des}
        disabled={disabled}
        concatenationSourceMap={activityModelInput.concatenation_source_map}
        enableQRCodeReader={activityModelInput.enable_qr_code_reader}
        enableBarcodeReader={activityModelInput.enable_barcode_reader}
        scannerFormats={activityModelInput.scanner_formats}
        {...{ isFetchingBlueprints }}
      />
    )
  }

  if (activityModelInput.content_type === 'autocomplete_uniselection') {
    return (
      <ActivityAutocompleteUniselection
        parentPath={parentPath}
        statusClass={statusClass}
        filteraction={activityModelInput.information_request_filteraction}
        filteractionParams={
          activityModelInput.information_request_filteraction_query_params
        }
        dataSources={activityModelInput.data_sources}
        endpoint={activityModelInput.information_request_callback}
        value={value || ''}
        onChange={handleChange}
        required={activityModelInput.required}
        label={activityModelInput.display_text.des}
        informational={informational}
        placeholder={activityModelInput.explanation_text?.des}
        actions={activityModelInput.actions || []}
        disabled={disabled}
        concatenationSourceMap={activityModelInput.concatenation_source_map}
        enableQRCodeReader={activityModelInput.enable_qr_code_reader}
        enableBarcodeReader={activityModelInput.enable_barcode_reader}
        scannerFormats={activityModelInput.scanner_formats}
        {...{ isFetchingBlueprints }}
      />
    )
  }

  if (activityModelInput.content_type === 'standard_uniselection') {
    return (
      <ActivityStandardUniselection
        id={activityModelInput.uuid}
        parentPath={parentPath}
        value={value || ''}
        options={activityModelInput.selection_options}
        filteraction={activityModelInput.information_request_filteraction}
        filteractionParams={
          activityModelInput.information_request_filteraction_query_params
        }
        label={activityModelInput.display_text.des}
        required={activityModelInput.required}
        onChange={handleChange}
        statusClass={
          statusClass as ActivityStandardUniselectionProps['statusClass']
        }
        placeholder={activityModelInput?.explanation_text?.des}
        disabled={disabled}
        informational={informational}
        actionButton={activityModelInput.action_button}
      />
    )
  }

  if (activityModelInput.content_type === 'standard_multiselection') {
    return (
      <ActivityStandardMultiselection
        id={activityModelInput.uuid}
        parentPath={parentPath}
        value={value || ''}
        options={activityModelInput.selection_options}
        filteraction={activityModelInput.information_request_filteraction}
        filteractionParams={
          activityModelInput.information_request_filteraction_query_params
        }
        label={activityModelInput.display_text.des}
        required={activityModelInput.required}
        onChange={handleChange}
        statusClass={
          statusClass as ActivityStandardMultiselectionProps['statusClass']
        }
        placeholder={activityModelInput?.explanation_text?.des}
        disabled={disabled}
        informational={informational}
        actionButton={activityModelInput.action_button}
      />
    )
  }

  if (activityModelInput.content_type === 'one_time_password') {
    return (
      <ActivityOTP
        id={activityModelInput.uuid}
        value={value}
        label={activityModelInput.display_text.des}
        length={activityModelInput.length}
        required={activityModelInput.required}
        onChange={handleChange}
        statusClass={statusClass as ActivityOTP['statusClass']}
        informational={informational}
      />
    )
  }

  if (activityModelInput.content_type === 'stars_evaluation') {
    return (
      <ActivityStarsEvaluation
        des={activityModelInput.display_text.des}
        complement={activityModelInput.explanation_text?.des || ''}
        nStars={activityModelInput.n_stars}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        informational={informational}
      />
    )
  }

  if (activityModelInput.content_type === 'explicit_uniselection_accordion') {
    return (
      <ActivityExplicitUniselectionAccordion
        id={activityModelInput.uuid}
        parentPath={parentPath}
        value={value || ''}
        options={activityModelInput.selection_options}
        filteraction={activityModelInput.information_request_filteraction}
        filteractionParams={
          activityModelInput.information_request_filteraction_query_params
        }
        hideAccordionArrowOnDisable={
          activityModelInput.hideAccordionArrowOnDisable
        }
        required={activityModelInput.required}
        onChange={handleChange}
        status={
          statusClass as ActivityExplicitUniselectionAccordionProps['status']
        }
      />
    )
  }

  if (activityModelInput.content_type === 'explicit_multiselection_accordion') {
    return (
      <ActivityMultiselectionAccordion
        id={activityModelInput.uuid}
        parentPath={parentPath}
        value={value || ''}
        options={activityModelInput.selection_options}
        filteraction={activityModelInput.information_request_filteraction}
        filteractionParams={
          activityModelInput.information_request_filteraction_query_params
        }
        hideAccordionArrowOnDisable={
          activityModelInput.hideAccordionArrowOnDisable
        }
        required={activityModelInput.required}
        onChange={handleChange}
        status={statusClass as ActivityMultiselectionAccordionProps['status']}
      />
    )
  }

  if (activityModelInput.content_type === '10_rating_evaluation') {
    return (
      <ActivityEvaluation
        title={activityModelInput.display_text.des}
        subtitle={activityModelInput.explanation_text?.des || ''}
        direction={activityModelInput.direction}
        value={value}
        onChange={handleChange}
        informational={activityModelInput.informational}
      />
    )
  }

  if (activityModelInput.content_type === 'multiple_answers') {
    return (
      <ActivityMultipleAnswers
        label={activityModelInput.display_text?.des}
        value={stringInputValue}
        onChange={handleChange}
        required={activityModelInput.required}
        statusClass={statusClass}
        readOnly={activityModelInput.informational}
        disabled={disabled}
        helpText={activityModelInput.help_text?.des}
        explanationText={activityModelInput.explanation_text?.des}
      />
    )
  }

  if (activityModelInput.content_type === 'embedded_sitemodel') {
    return (
      <ActivityEmbeddedSitemodel
        sitemodelUuid={activityModelInput.sitemodel_uuid}
        customQueryString={activityModelInput.custom_query_string}
        urlSiteRenderer={window.urlSiteRenderer || ''}
      />
    )
  }

  const isStringAsAutocomplete = Boolean(
    activityModelInput.information_request_callback ||
    activityModelInput.information_request_filteraction
  )

  if (isStringAsAutocomplete) {
    return (
      <ActivityAutocompleteUniselection
        parentPath={parentPath}
        statusClass={statusClass}
        filteraction={activityModelInput.information_request_filteraction}
        filteractionParams={
          activityModelInput.information_request_filteraction_query_params
        }
        dataSources={activityModelInput.data_sources}
        endpoint={activityModelInput.information_request_callback}
        value={value || ''}
        onChange={handleChange}
        required={activityModelInput.required}
        label={activityModelInput.display_text.des}
        informational={informational}
        disabled={disabled}
        concatenationSourceMap={activityModelInput.concatenation_source_map}
        scannerFormats={activityModelInput.scanner_formats}
        actions={[]}
      />
    )
  }

  const isStringAsSelect = Boolean(
    activityModelInput.information_request_textexpression
  )

  if (isStringAsSelect) {
    return (
      <ActivityInputSelect
        id={activityModelInput.uuid}
        onChange={handleChange}
        options={activityModelInput.information_request_textexpression}
        inputValue={stringInputValue}
        label={activityModelInput.display_text.des}
        informational={informational}
        required={activityModelInput.required}
        helpText={activityModelInput.help_text?.des}
        explanationText={activityModelInput.explanation_text?.des}
        statusClass={statusClass}
        disabled={disabled}
        {...{ isFetchingBlueprints }}
      />
    )
  }

  return (
    <ActivityInputString
      id={activityModelInput.uuid}
      statusClass={statusClass}
      value={stringInputValue}
      required={activityModelInput?.required}
      onChange={handleChange}
      label={activityModelInput?.display_text?.des}
      informational={informational}
      helpText={activityModelInput?.help_text?.des}
      explanationText={activityModelInput?.explanation_text?.des}
      formatMask={activityModelInput.format_mask}
      actionButton={activityModelInput.action_button}
      enableQRCodeReader={activityModelInput.enable_qr_code_reader}
      enableBarcodeReader={activityModelInput.enable_barcode_reader}
      scannerFormats={activityModelInput.scanner_formats}
      {...{ isFetchingBlueprints, disabled }}
    />
  )
}

export type ActivityProps = {
  activityModelInput: FlatActivityModel
  value?: ActivityValue
  parentPath?: string[]
  currentStepUuid?: string
  disabled?: boolean
  changeActivity: (
    uuid: string,
    value: { id: string; des: string } | string
  ) => void
  validateGroupActivity?: (
    activityUuid: string,
    validationStatus?: ValidationStatus
  ) => void
}

export default function Activity({
  activityModelInput,
  value,
  changeActivity,
  validateGroupActivity,
  parentPath,
  currentStepUuid,
  disabled
}: ActivityProps) {
  const isActivityGroup = activityModelInput.content_type === 'activity_group'

  const { validationStatus: syncValidationStatus, helperText: syncHelperText } =
    useValidateActivity({
      activityModel: activityModelInput,
      value,
      enabled: !isActivityGroup
    })

  const {
    validationStatus: callbackValidationStatus,
    helperText: callbackHelperText,
    triggerValidation
  } = useValidateActivityCallback({
    activityModel: activityModelInput,
    value,
    enabled: !isActivityGroup
  })

  const validationStatus =
    syncValidationStatus === VALIDATION_STATUSES.INVALID
      ? syncValidationStatus
      : (callbackValidationStatus ?? syncValidationStatus)

  const helperText =
    syncValidationStatus === VALIDATION_STATUSES.INVALID
      ? syncHelperText
      : (callbackHelperText ?? syncHelperText)

  useEffect(() => {
    validateGroupActivity?.(activityModelInput.uuid, validationStatus)
  }, [activityModelInput.uuid, validateGroupActivity, validationStatus])

  const inputClassByStatus: Record<ValidationStatus, string> = {
    [VALIDATION_STATUSES.VALID]: 'is-valid',
    [VALIDATION_STATUSES.INVALID]: 'is-invalid',
    [VALIDATION_STATUSES.PENDING]: ''
  }

  const inputClass = inputClassByStatus[validationStatus]

  const statusClassByValidation: Record<ValidationStatus, StatusClass> = {
    [VALIDATION_STATUSES.VALID]: 'valid',
    [VALIDATION_STATUSES.INVALID]: 'error',
    [VALIDATION_STATUSES.PENDING]: 'default'
  }

  const statusClass = statusClassByValidation[validationStatus]

  const informationRequestSitemodel =
    activityModelInput.information_request_sitemodel

  const { executeBlueprints, isFetchingBlueprints } = useBlueprintsContext()

  const [previousValue, setPreviousValue] = useState<
    ActivityModel['value'] | false
  >(false)

  const handleBlur: FocusEventHandler = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) {
      return
    }
    if (!isActivityGroup) {
      triggerValidation()
    }
    if (previousValue !== value && !isActivityGroup) {
      executeBlueprints({
        type: 'activity_changed',
        activityValue: value,
        activityModelUuid: activityModelInput.uuid,
        parentPath
      })
    }
  }

  const handleFocus = () => {
    setPreviousValue(value)
  }

  const handleChange = useCallback(
    (newValue: ActivityValue) => {
      changeActivity(activityModelInput.uuid, newValue)
    },
    [activityModelInput.uuid, changeActivity]
  )

  return (
    <div
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-testid='activity'
      className={`form-group ${helperText && 'has-validation'}`}
    >
      {informationRequestSitemodel && (
        <SiteRenderer
          sitemodelUuid={informationRequestSitemodel}
          urlSiteRenderer={window.urlSiteRenderer || ''}
        />
      )}

      <ActivityInputAnimatedWrapper isHidden={activityModelInput.hidden}>
        <ActivityInput
          {...{
            parentPath,
            activityModelInput,
            value,
            handleChange,
            inputClass,
            currentStepUuid,
            statusClass,
            isFetchingBlueprints,
            disabled
          }}
        />
      </ActivityInputAnimatedWrapper>
    </div>
  )
}
